import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';

import { useDispatch, useSelector } from '../../store/utils';
import { Group } from '../../organisms/group';
import { colors } from '../../theme/palette';
import { Close } from '../../atoms/icons/close';
import { variablesChange } from '../../store/actions';
import { DialogClose } from '../../atoms/dialog-close';
import { useInitialWizardDialog } from './initial-wizard-dialog';

type Props = {
  variant?: 'settings' | 'default';
  sx?: SxProps;
};

export const InitialWizardBanner = ({
  variant = 'default',
  sx = {},
}: Props) => {
  const [wizardCompletedAt, bannerHiddenPermanently] = useSelector(
    ({
      variables: {
        INITIAL_WIZARD_COMPLETED_AT,
        INITIAL_WIZARD_BANNER_HIDDEN_PERMANENTLY,
      },
    }) =>
      [
        INITIAL_WIZARD_COMPLETED_AT,
        INITIAL_WIZARD_BANNER_HIDDEN_PERMANENTLY,
      ] as const
  );

  const { dialog, open } = useInitialWizardDialog();
  const { onHide, hideDialog } = useHideWizardBanner({
    openWizardDialog: open,
  });

  // Hide banner if:
  // - Wizard is completed
  // - Banner is hidden permanently and not in settings view
  if (
    variant !== 'settings' &&
    (!!wizardCompletedAt || bannerHiddenPermanently)
  ) {
    return null;
  }

  const content = (
    <>
      <Typography
        variant="h3"
        sx={{ color: variant === 'settings' ? 'text.primary' : 'common.white' }}
      >
        {wizardCompletedAt ? (
          <FormattedMessage
            id="initial_wizard.banner.title.completed"
            defaultMessage="Projít počáteční nastavení"
          />
        ) : (
          <FormattedMessage
            id="initial_wizard.banner.title"
            defaultMessage="Dokončete počáteční nastavení"
          />
        )}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          maxWidth: 400,
          mb: 2,
          color: variant === 'settings' ? 'text.primary' : 'common.white',
        }}
      >
        <FormattedMessage
          id="initial_wizard.banner.subtitle"
          defaultMessage="Průvodce vám pomůže nastavit základní parametry vašeho systému."
        />
      </Typography>

      <Box>
        <Button
          variant="contained"
          size="small"
          onClick={open}
          sx={{ textTransform: 'none' }}
        >
          <FormattedMessage
            id="initial_wizard.banner.button"
            defaultMessage="Spustit průvodce"
          />
        </Button>
      </Box>
    </>
  );

  return (
    <>
      <Group layout="list" sx={{ ...sx }}>
        <Paper
          sx={{
            bgcolor: (theme) =>
              variant === 'settings'
                ? 'background.paper'
                : theme.palette.mode === 'light'
                ? 'text.disabled'
                : '#343e42',
            color: colors.white,
            position: 'relative',
          }}
        >
          <Stack gap={1}>{content}</Stack>

          {variant !== 'settings' && (
            <IconButton
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                cursor: 'pointer',
                zIndex: 1,
                color: colors.white,
              }}
              onClick={onHide}
            >
              <Close />
            </IconButton>
          )}
        </Paper>
      </Group>

      {dialog}

      {hideDialog}
    </>
  );
};

const useHideWizardBanner = ({
  openWizardDialog,
}: {
  openWizardDialog: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const onClose = () => setOpen(false);

  const dispatch = useDispatch();

  const showMore = () => {
    openWizardDialog();
    onClose();
  };

  const hidePermanently = () => {
    dispatch(
      variablesChange({ INITIAL_WIZARD_BANNER_HIDDEN_PERMANENTLY: true })
    );
    onClose();
  };

  const hideDialog = (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      slotProps={{ paper: { sx: { bgcolor: 'secondary.main' } } }}
    >
      <DialogClose onClose={onClose} />
      <DialogTitle variant="h2" sx={{ textAlign: 'center', py: 3 }}>
        <FormattedMessage
          id="initial_wizard.banner.hide.confirm"
          defaultMessage="Opravdu chcete skrýt průvodce?"
        />
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ textAlign: 'center', mt: 1 }}>
          <FormattedMessage
            id="initial_wizard.banner.hide.confirm.description"
            defaultMessage="Průvodce můžete kdykoli znovu spustit v nastavení aplikace."
          />
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          py: 2,
          flexDirection: 'column',
          gap: 1.5,
          '& .MuiButton-root': { width: 280 },
        }}
      >
        <Button onClick={showMore} color="primary" variant="contained">
          <FormattedMessage
            id="initial_wizard.banner.hide.show_more"
            defaultMessage="Spustit průvodce"
          />
        </Button>
        <Button onClick={hidePermanently} variant="text">
          <FormattedMessage
            id="initial_wizard.banner.hide.yes"
            defaultMessage="Skrýt"
          />
        </Button>
      </DialogActions>
    </Dialog>
  );

  return {
    onHide: () => setOpen(true),
    hideDialog,
  };
};
